import { usePermissions } from "@/hooks/usePermissions"
import { Alert, Box } from "@chakra-ui/react"
import type React from "react"

interface PermissionGateProps {
  children: React.ReactNode
  // New scope-based props
  scope?: string
  scopes?: string[]
  // Legacy props (for backward compatibility)
  permission?: string
  permissions?: string[]
  requireAll?: boolean
  adminOverride?: boolean
  fallback?: React.ReactNode
  showForbidden?: boolean
}

export const PermissionGate = ({
  children,
  scope,
  scopes,
  permission,
  permissions,
  requireAll = false,
  adminOverride = true,
  fallback = null,
  showForbidden = true,
}: PermissionGateProps) => {
  const {
    // New scope-based methods
    hasScope,
    hasAnyScope,
    hasAllScopes,
    isAdmin,
    // Legacy methods (for backward compatibility)
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = usePermissions()

  let hasAccess = false

  if (adminOverride && isAdmin) {
    hasAccess = true
  } else if (scope) {
    // New scope-based checking
    hasAccess = hasScope(scope)
  } else if (scopes && scopes.length > 0) {
    // New scopes-based checking
    hasAccess = requireAll ? hasAllScopes(scopes) : hasAnyScope(scopes)
  } else if (permission) {
    // Legacy permission checking
    hasAccess = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    // Legacy permissions checking
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)
  }

  if (hasAccess) {
    return <>{children}</>
  }

  if (fallback) {
    return <>{fallback}</>
  }

  if (showForbidden) {
    return (
      <Box p={8}>
        <Alert.Root status="warning" variant="subtle">
          <Alert.Indicator />
          <Box>
            <Alert.Title>Access Denied!</Alert.Title>
            <Alert.Description>
              You don't have permission to access this resource.
              {scope && ` Required scope: ${scope}`}
              {scopes &&
                scopes.length > 0 &&
                ` Required scopes: ${scopes.join(", ")}`}
              {permission && ` Required permission: ${permission}`}
              {permissions &&
                permissions.length > 0 &&
                ` Required permissions: ${permissions.join(", ")}`}
            </Alert.Description>
          </Box>
        </Alert.Root>
      </Box>
    )
  }

  return null
}

export default PermissionGate
